import { call, put, takeLatest } from "redux-saga/effects";
import { IBaseResponse } from "../../../interfaces/base";
import {
  ICreateExerciseAction,
  IDeleteExerciseAction,
  IGetExerciseAction,
  IUpdateExerciseAction,
} from "../../../interfaces/store/exercise-interface";
import {
  getExerciseFailed,
  getExerciseSuccess,
  getExerciseStart,
  createExerciseStart,
  createExerciseSuccess,
  createExerciseFailed,
  updateExerciseStart,
  updateExerciseSuccess,
  updateExerciseFailed,
  deleteExerciseStart,
  deleteExerciseFailed,
  deleteExerciseSuccess,
} from "./exercise-slice";
import {
  deleteExerciseApi,
  getExerciseApi,
  updateExerciseApi,
} from "../../../api/exercises";
import { createExerciseApi } from "../../../api/exercises";
import { IUploadFileParams } from "../../../interfaces/files/files";
import { uploadImage } from "../../utils/fileUpload";
import { base64ToBlob } from "../../../utils/utils";

function* _getExercise({ payload }: IGetExerciseAction) {
  try {
    const { exerciseId } = payload;

    if (!exerciseId) {
      yield put(getExerciseFailed({ message: "Exercise ID is required" }));
      return;
    }

    const exercise: IBaseResponse = yield call(getExerciseApi, exerciseId);

    if (exercise.error) {
      yield put(getExerciseFailed(exercise.error));
    }

    yield put(getExerciseSuccess(exercise.data));
  } catch (error: any) {
    yield put(getExerciseFailed({ message: error.message }));
  }
}

function* _createExercise({ payload }: ICreateExerciseAction) {
  try {
    const response: IBaseResponse = yield call(createExerciseApi, payload);

    const { imageData, videoData } = payload;

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile),
        }
      : undefined;

    const blobVideoData = videoData
      ? {
          ...videoData,
          binaryFile: base64ToBlob(videoData.binaryFile),
        }
      : undefined;

    yield call(handleMediaData, blobImageData, response.data._id, "image");
    yield call(handleMediaData, blobVideoData, response.data._id, "video");

    if (response.error) {
      yield put(createExerciseFailed(response.error));
    } else {
      yield put(createExerciseSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(createExerciseFailed({ message: error.message }));
  }
}

function* _updateExercise({ payload }: IUpdateExerciseAction) {
  try {
    const { exerciseId, exercise, imageData, videoData } = payload;

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile),
        }
      : undefined;

    const blobVideoData = videoData
      ? {
          ...videoData,
          binaryFile: base64ToBlob(videoData.binaryFile),
        }
      : undefined;

    if (blobImageData && blobImageData.binaryFile) {
      yield call(uploadImage, blobImageData, exerciseId);
    }

    if (blobVideoData && blobVideoData.binaryFile) {
      yield call(uploadImage, blobVideoData, exerciseId);
    }

    const updateBody = exercise;

    if (updateBody) {
      if (blobImageData?.fileName) {
        updateBody.image = exerciseId + "/" + blobImageData.fileName;
      } else if (blobImageData && blobImageData.binaryFile === null) {
        updateBody.image = null;
      }

      if (blobVideoData?.fileName) {
        updateBody.video = exerciseId + "/" + blobVideoData.fileName;
      } else if (blobVideoData && blobVideoData.binaryFile === null) {
        updateBody.video = null;
      }
    }

    const response: IBaseResponse = yield call(updateExerciseApi, {
      exerciseId,
      exercise,
    });

    if (response.error) {
      yield put(updateExerciseFailed(response.error));
    } else {
      yield put(updateExerciseSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(updateExerciseFailed({ message: error.message }));
  }
}

function* _deleteExercise({ payload }: IDeleteExerciseAction) {
  try {
    const response: IBaseResponse = yield call(
      deleteExerciseApi,
      payload.exerciseId
    );

    if (response.error) {
      yield put(deleteExerciseFailed(response.error));
    } else {
      yield put(deleteExerciseSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(deleteExerciseFailed({ message: error.message }));
  }
}

function* handleMediaData(
  mediaData: IUploadFileParams,
  responseId: string,
  mediaType: "image" | "video"
) {
  if (mediaData && responseId) {
    if (mediaData.binaryFile) {
      yield call(uploadImage, mediaData, responseId);
    }
    yield put(
      updateExerciseStart({
        exercise: {
          [mediaType]: mediaData.binaryFile
            ? responseId + "/" + mediaData.fileName
            : null,
        },
        exerciseId: responseId,
      })
    );
  }
}

function* exerciseSaga() {
  yield takeLatest(getExerciseStart.type, _getExercise);
  yield takeLatest(createExerciseStart.type, _createExercise);
  yield takeLatest(updateExerciseStart.type, _updateExercise);
  yield takeLatest(deleteExerciseStart.type, _deleteExercise);
}

export default exerciseSaga;
